import { useState, useEffect } from 'react';
import { getBuildingDetailsById, getDynamicOffers, updateBuilding } from '../../auth/_requests';
import { DynamicOffer, PaymentMethodType, UpdateDueDateTypeRequest } from '../utils/types';
import { DueDateTypeEnum, getBusinessDays } from '../utils/paymentUtils';

const ADVANCE_OFFER_CODES = [
  DueDateTypeEnum.ThreeWeekAdvance,
  DueDateTypeEnum.TwoWeekAdvance,
  DueDateTypeEnum.OneWeekAdvance,
] as const;

const WEEK_ORDER: Record<string, number> = {
  [DueDateTypeEnum.ThreeWeekAdvance]: 3,
  [DueDateTypeEnum.TwoWeekAdvance]: 2,
  [DueDateTypeEnum.OneWeekAdvance]: 1,
};

const getAdvancePaymentConfig = (
  businessDaysBeforeDueDate?: number | null,
  paymentMethod?: PaymentMethodType | null
): Record<string, { description: string }> => {
  const businessDays = `${getBusinessDays(businessDaysBeforeDueDate, paymentMethod)} business days`;
  return {
    [DueDateTypeEnum.ThreeWeekAdvance]: {
      description: `Pay rent 3 weeks + ${businessDays} before the due date and earn the highest points`,
    },
    [DueDateTypeEnum.TwoWeekAdvance]: {
      description: `Pay rent 2 weeks + ${businessDays} before the due date and earn more points`,
    },
    [DueDateTypeEnum.OneWeekAdvance]: {
      description: `Pay rent 1 week + ${businessDays} before the due date and earn points`,
    },
  };
};

const getZeroPointOption = (
  businessDaysBeforeDueDate?: number | null,
  paymentMethod?: PaymentMethodType | null
) => {
  const businessDays = `${getBusinessDays(businessDaysBeforeDueDate, paymentMethod)} business days`;
  return {
    points: 'Setup Autopay earlier to earn points',
    description: `Pay ${businessDays} before due date`,
    code: DueDateTypeEnum.SubscriptionPayment3BusinessDaysBeforeDueDate,
    dueDate: null as number | null,
  };
};

export function useSubscriptionPoints(
  token: string | undefined,
  rentAmount?: number,
  tenantBuildingId?: string,
  paymentMethodId?: string | null,
  cardCompanyTypeId?: string | null,
  paymentMethod?: PaymentMethodType | null,
  businessDaysBeforeDueDate?: number | null
) {
  const [loading, setLoading] = useState(false);
  const [pointsDescription, setPointsDescription] = useState<
    { points: string; description: string; code: string; dueDate: number | null }[]
  >([]);
  const [selectedDueDateType, setSelectedDueDateType] = useState<string>(
    DueDateTypeEnum.SubscriptionPayment3BusinessDaysBeforeDueDate
  );
  const [selectedDueDate, setSelectedDueDate] = useState(1);
  const [effectiveRentAmount, setEffectiveRentAmount] = useState<number | undefined>(rentAmount);

  useEffect(() => {
    if (!token || !tenantBuildingId) return;
    const fetchBuildingDetails = async () => {
      try {
        setLoading(true);
        const response = await getBuildingDetailsById(token, tenantBuildingId);
        const buildingData = response.data?.[0];
        setSelectedDueDateType(
          buildingData?.dueDateType?.name ||
            DueDateTypeEnum.SubscriptionPayment3BusinessDaysBeforeDueDate
        );
        setSelectedDueDate(buildingData?.building?.dueDate);
        if (!rentAmount && buildingData?.building?.rentAmount) {
          setEffectiveRentAmount(buildingData.building.rentAmount);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBuildingDetails();
  }, [token, tenantBuildingId]);

  useEffect(() => {
    if (!token || !effectiveRentAmount) return;
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await getDynamicOffers(
          token,
          effectiveRentAmount,
          paymentMethodId,
          cardCompanyTypeId
        );

        const advancePaymentConfig = getAdvancePaymentConfig(
          businessDaysBeforeDueDate,
          paymentMethod
        );

        const advanceOffers = (response.data ?? [])
          .filter((offer: DynamicOffer) =>
            ADVANCE_OFFER_CODES.includes(offer.code as (typeof ADVANCE_OFFER_CODES)[number])
          )
          .sort(
            (a: DynamicOffer, b: DynamicOffer) =>
              (WEEK_ORDER[b.code] ?? 0) - (WEEK_ORDER[a.code] ?? 0)
          );

        const descriptions = advanceOffers.map((offer: DynamicOffer) => {
          const tickets = offer.tickets && Number(offer.tickets) > 0 ? Number(offer.tickets) : 0;
          const ticketText = tickets > 0 ? ` + ${tickets} Ticket${tickets > 1 ? 's' : ''}` : '';
          return {
            points: `Earn ${offer.dynamicPoints} TP Points${ticketText}`,
            description: advancePaymentConfig[offer.code]?.description ?? 'Advance rent payment',
            code: offer.code,
            dueDate: null as number | null,
          };
        });

        setPointsDescription([
          ...descriptions,
          getZeroPointOption(businessDaysBeforeDueDate, paymentMethod),
        ]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [
    token,
    effectiveRentAmount,
    paymentMethodId,
    cardCompanyTypeId,
    paymentMethod,
    businessDaysBeforeDueDate,
  ]);

  const updateDueDate = async () => {
    try {
      setLoading(true);
      await updateBuilding(token, tenantBuildingId, {
        buildingDetails: {
          dueDateTypeName: selectedDueDateType,
          dueDate: selectedDueDate ?? 1,
        },
      } as UpdateDueDateTypeRequest);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    pointsDescription,
    selectedDueDateType,
    selectedDueDate,
    setSelectedDueDateType,
    setSelectedDueDate,
    updateDueDate,
  };
}
